import {
  Page,
  BlockStack,
  Card,
  SkeletonBodyText,
  SkeletonDisplayText,
} from "@medentic/polaris-react";
import { SettingsPageWrapper } from "@shared/ui/Settings";
import { useTranslation } from "react-i18next";
import { EntityType } from "@shared/types/commonTypes";
import { useParams } from "react-router-dom";
import { SubscriptionData } from "./types";
import { CurrentPlanCard } from "./components/ActiveSubscriptionPlanCard";
import { AvailablePlansCard } from "./components/SubscriptionPlanListCard";
import { StorageOptionsCard } from "./components/StorageAddOnListCard";
import { CreditsCard } from "./components/CreditsCard";
import { InvoiceListCard } from "./components/InvoiceListCard";
import { useQuery } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";

export default function SettingsSubscription() {
  const { t } = useTranslation();
  const { clinicId } = useParams<{ clinicId: string }>();
  const { GET } = useApiRequest();
  const resolvedClinicId = clinicId ?? "";
  const { data: subscriptionData, isLoading: isLoadingSubscription } =
    useQuery<SubscriptionData>({
      queryKey: ["subscription", clinicId],
      queryFn: () => GET(`/api/healthcare-companies/${clinicId}/billing/subscription`),
      enabled: !!clinicId,
    });

  const renderLoadingSkeleton = () => (
    <SettingsPageWrapper entityType={EntityType.CLINIC} entityId={resolvedClinicId}>
      <Page title={t("settings.subscription.title")}>
        <BlockStack gap="400">
          {/* Current Plan Card Skeleton */}
          <Card>
            <BlockStack gap="400">
              <SkeletonDisplayText size="medium" />
              <SkeletonBodyText lines={3} />
            </BlockStack>
          </Card>

          {/* Available Plans Card Skeleton */}
          <Card>
            <BlockStack gap="400">
              <SkeletonDisplayText size="medium" />
              <SkeletonBodyText lines={2} />
              <SkeletonBodyText lines={2} />
            </BlockStack>
          </Card>

          {/* Storage Options Card Skeleton */}
          <Card>
            <BlockStack gap="400">
              <SkeletonDisplayText size="medium" />
              <SkeletonBodyText lines={3} />
              <SkeletonBodyText lines={2} />
            </BlockStack>
          </Card>

          {/* Credits Card Skeleton */}
          <Card>
            <BlockStack gap="400">
              <SkeletonDisplayText size="medium" />
              <SkeletonBodyText lines={2} />
              <SkeletonBodyText lines={3} />
              <SkeletonBodyText lines={4} />
            </BlockStack>
          </Card>

          {/* Invoice List Card Skeleton */}
          <Card>
            <BlockStack gap="400">
              <SkeletonDisplayText size="medium" />
              <SkeletonBodyText lines={4} />
            </BlockStack>
          </Card>
        </BlockStack>
      </Page>
    </SettingsPageWrapper>
  );

  if (isLoadingSubscription) {
    return renderLoadingSkeleton();
  }

  if (!subscriptionData) {
    return null;
  }

  return (
    <SettingsPageWrapper entityType={EntityType.CLINIC} entityId={resolvedClinicId}>
      <Page title={t("settings.subscription.title")}>
        <BlockStack gap="400">
          <CurrentPlanCard
            clinicId={clinicId!}
            data={subscriptionData}
          />

          <AvailablePlansCard
            clinicId={clinicId!}
            data={subscriptionData}
            mode={subscriptionData.currentSubscription.mode}
          />

          <StorageOptionsCard
            data={subscriptionData}
            clinicId={clinicId!}
          />

          <CreditsCard
            data={subscriptionData}
            clinicId={clinicId!}
          />

          <InvoiceListCard clinicId={clinicId!} />

        </BlockStack>
      </Page>
    </SettingsPageWrapper>
  );
}
